import { Fragment, type ReactNode } from "react";

/**
 * Minimal, dependency-free renderer for the markdown-lite used across help
 * content (articles, page-guide steps, panel help). Supports `#`/`##` headings,
 * `-` bullets, blank-line spacing, and inline `**bold**`. Deliberately not a
 * full markdown engine — help copy is authored to this small subset.
 */
export function HelpMarkdown({ content, className }: { content: string; className?: string }) {
  const lines = content.split("\n");
  return (
    <div className={className ?? "space-y-1 text-sm text-fg-muted"}>
      {lines.map((line, i) => {
        const key = `${i}:${line}`;
        if (line.startsWith("# ")) {
          return (
            <h3 key={key} className="mt-2 text-base font-bold text-fg">
              {renderInline(line.slice(2))}
            </h3>
          );
        }
        if (line.startsWith("## ")) {
          return (
            <h4 key={key} className="mt-2 text-sm font-semibold text-fg">
              {renderInline(line.slice(3))}
            </h4>
          );
        }
        if (line.startsWith("- ")) {
          return (
            <li key={key} className="ml-4 list-disc">
              {renderInline(line.slice(2))}
            </li>
          );
        }
        if (line.trim() === "") {
          return <div key={key} className="h-2" aria-hidden="true" />;
        }
        return <p key={key}>{renderInline(line)}</p>;
      })}
    </div>
  );
}

/** Render inline `**bold**` segments; everything else is plain text. */
function renderInline(text: string): ReactNode {
  const parts = text.split(/(\*\*[^*]+\*\*)/g);
  return parts.map((part, i) => {
    if (part.startsWith("**") && part.endsWith("**")) {
      return (
        <strong key={i} className="font-semibold text-fg">
          {part.slice(2, -2)}
        </strong>
      );
    }
    return <Fragment key={i}>{part}</Fragment>;
  });
}
